import type { Upgrade } from '../types/index';

export const UPGRADES: Upgrade[] = [
  {
    id: 1,
    name: "Upgrade your GPU",
    description: "Buy the newest consumer graphics card to boost your training rate and improve your model",
    category: "production",
    cost: 500,
    moneyPerSecond: 25,
    pollutionPerSecond: 15
  },
  {
    id: 2,
    name: "A New PC",
    description: "Buy a brand new top-of-the-range PC to further increase your training rate and improve your model",
    category: "production",
    cost: 3000,
    moneyPerSecond: 150,
    pollutionPerSecond: 85,
  },
  {
    id: 3,
    name: "Local Server Rack",
    description: "A new rack of local servers to further increase your training capability.",
    category: "production",
    cost: 6500,
    moneyPerSecond: 350,
    pollutionPerSecond: 175
  },
  {
    id: 4,
    name: "Build a New Datacentre",
    description: "The pinnacle in AI hardware, a brand new server farm to host your models for the masses",
    category: "production",
    cost: 12000,
    moneyPerSecond: 600,
    pollutionPerSecond: 500,
  },
  {
    id: 5,
    name: "Add Image Generation Functionality",
    description: "Expand your AI service by introducing an image generation model",
    category: "production",
    cost: 15000,
    moneyPerSecond: 1000,
    pollutionPerSecond: 750
  },

  // -------------------------------------------------------------
  // PERCEPTION UPGRADES (Bad)
  // -------------------------------------------------------------
 {
    id: 6,
    name: "Unsubstantiated AI Climate Claims",
    description: "Market the green benefits of your AI products without any real evidence or lifecycle analysis to back it up.",
    category: "perception",
    cost: 1500,
    perceptionImpact: 3,
    educationalMessage: "AI companies have frequently made bold claims about their products' potential to reduce emissions, such as optimizing energy use or improving supply chain efficiency. However, these claims are often based on theoretical models without any real-world testing or lifecycle analysis, and they ignore the massive emissions generated by training and running large AI models.",
    realWorldEvidence: "Only 26% of AI CLimate claims cite research and 36% provide no evidence at all, according to Climate non-profit Beyond Fossil Fuels.",
    source: "Euronews",
    sourceUrl: "https://www.euronews.com/next/2026/02/17/ai-greenwashing-most-of-big-techs-ai-climate-promises-fall-flat-study-finds"
  },
  {
    id: 7,
    name: "Misleading Progress Narrative",
    description: "Use some creative accounting to show a 'decrease' in datacentre emissions.",
    category: "perception",
    cost: 2500,
    perceptionImpact: 6,
    educationalMessage: "Companies often report 'emissions intensity' (emissions per unit of production) instead of absolute emissions, and abuse carbon credits, allowing them to claim progress even as their total carbon footprint skyrockets.",
    realWorldEvidence: "Google's 2025 Sustainability Report noted a 17% reduction in datacentre emissions desipte the fact that their total electricity usage increased by 23%, this is only possible due to clever climate accounting",
    source: "Google's 2025 Sustainability Report",
    sourceUrl: "https://www.gstatic.com/gumdrop/sustainability/google-2025-environmental-report.pdf"
  },
  {
    id: 8,
    name: "Enable Others to 'Reduce' Emissions",
    description: "Boast about the climate benefits your products have given to customers without any real evidence.",
    category: "perception",
    cost: 5000,
    perceptionImpact: 9,
    educationalMessage: "Tech companies are more than happy to claim when their products help sustainability. Unfortunately they also love to oversell it.",
    realWorldEvidence: "In 2025, Google claimed that their products helped to reduce emissions by over 26 million tons of CO₂ however, half of this figure originates from numbers reported by corporate partners who use Google Earth Pro. A dubious claim at best.",
    source: "Google's 2025 Sustainability report",
    sourceUrl: "https://sustainability.google/stories/how-google-earth-pro-helps-clean-energy-development/"
  },
  

  
  // -------------------------------------------------------------
  // PERCEPTION UPGRADES (Good)
  // -------------------------------------------------------------
  {
    "id": 9,
    "name": "Real-Time Energy Matching",
    "description": "Guarantee that every hour of power your AI consumes is matched by an hour of clean energy generated on the same local grid.",
    "category": "perception",
    "cost": 8000,
    "perceptionImpact": 15,
    "educationalMessage": "24/7 CFE forces companies to support local clean energy rather than just buying cheap credits from across the globe.",
    "realWorldEvidence": "Major tech companies such as, Google and Microsoft have shifted toward 24/7 CFE. 24/7 matching requires the energy to be clean at the exact moment of use, whereas traditional offsetting permits for balancing non green energy with green energy between different countries.",
    "source": "24/7 by 2030: Realizing a Carbon-free Future",
    "sourceUrl": "https://www.gstatic.com/gumdrop/sustainability/247-carbon-free-energy.pdf"
  },
  {
    "id": 10,
    "name": "Model Distillation",
    "description": "Use a massive model to train a smaller, distilled and efficient model with similar performance for a fraction of the carbon cost.",
    "category": "perception",
    "cost": 4000,
    "perceptionImpact": 6,
    "educationalMessage": "Distillation allows us to significantly reduce energy per request, by distilling bigger models allowing them to be run with minimal hardware as opposed to server farms.",
    "realWorldEvidence": "DistilBERT a distilled version of BERT retains about 97% of the performance, while being 40% smaller and 60% faster.",
    "source": "DistilBERT, a distilled version of BERT: smaller, faster, cheaper and lighter",
    "sourceUrl": "https://arxiv.org/abs/1910.01108"
  },
  {
    "id": 11,
    "name": "Cloud-Powered Radiators",
    "description": "Capture and use waste heat from your server racks to provide free sustainable heating to the neighborhood.",
    "category": "perception",
    "cost": 12000,
    "perceptionImpact": 20,
    "educationalMessage": "Data centers generate a great quantity of heat due to the hardware. Instead of using more energy to cool the hardware, the heat can be used to replace gas boilers in nearby housing.",
    "realWorldEvidence": "The Tallaght District Heating Scheme in Ireland uses waste heat from an Amazon Web Services data center.",
    "source": "Sustainable Energy Authority of Ireland",
    "sourceUrl": "https://www.seai.ie/case-studies/tallaght-district-heating/"
  },
  {
    "id": 12,
    "name": "Publish an Energy and Carbon Dashboard",
    "description": "Publicly show real time data on energy use, carbon emissions, and water use across your AI services.",
    "category": "perception",
    "cost": 5000,
    "perceptionImpact": 9,
    "educationalMessage":
      "Transparency makes sustainability claims more credible. Publishing operational metrics allows outsiders to judge whether the company is actually improving its environmental performance rather than relying on vague branding.",
    "realWorldEvidence":
      "Green software guidance stresses measurement and observability, including dashboards that continuously track resource and energy related metrics.",
    "source": "Green Software Practices",
    "sourceUrl": "https://www.greenseal.dev/gsp/",
  },
  // { Not sure if this is exactly a perception upgrade though.
  //   "id": 13,
  //   "name": "Carbon Intensity Aware Scheduling",
  //   "description": "Shift flexible computing tasks to times when the electricity grid is cleaner.",
  //   "category": "perception",
  //   "cost": 7000,
  //   "perceptionImpact": 20,
  //   "educationalMessage":
  //     "Not all electricity is equally carbon intensive at every hour. Delaying flexible workloads to cleaner periods can lower emissions without reducing the amount of useful work completed.",
  //   "realWorldEvidence":
  //     "Carbon aware datacentre research shows that flexible jobs can be shifted in time to reduce emissions when grid carbon intensity is lower.",
  //   "source": "Carbon-Aware Computing for Datacenters",
  //   "sourceUrl": "https://sites.google.com/view/energy-efficiency-languages",
  // },
  {
    "id": 14,
    "name": "Green Software Practices Certification",
    "description": "Introduce organization wide sustainability practices for software development, from training and measurement to engineering standards and continuous improvement.",
    "category": "perception",
    "cost": 8000,
    "perceptionImpact": 15,
    "educationalMessage":
      "Sustainable software should be built into the way a company operates, that includes governance, training, measurement and engineering standards help make sustainability part of everyday development rather than a one off campaign.",
    "realWorldEvidence":
      "Green Software Practices focuses on governance and culture, training and knowledge, measurement and observability, engineering practices, and improvement and learning.",
    "source": "Green Software Practices",
    "sourceUrl": "https://www.greenseal.dev/gsp/",
  },
  // { This was mentioned in the slides iirc, but again, not necessarily a perception upgrade
  //   "id": 16,
  //   "name": "Data-Centric Cleanup Pipeline",
  //   "description": "Clean, deduplicate, and reduce your dataset before training instead of endlessly scaling up raw data.",
  //   "category": "perception",
  //   "cost": 6000,
  //   "perceptionImpact": 17,
  //   "educationalMessage":
  //     "Data centric AI focuses on improving data quality rather than only increasing model size or compute. Smaller and cleaner datasets can improve efficiency without sacrificing performance.",
  //   "realWorldEvidence":
  //     "Green AI teaching material emphasizes that small but reliable datasets can be preferable to larger noisy datasets and that data reduction can lower energy use.",
  //   "source": "Green AI",
  //   "sourceUrl": "https://www.youtube.com/watch?v=06-AZXmwHjo",
  // },
  {
    "id": 17,
    "name": "Hardware Reuse Initiative",
    "description": "Maintain compatibility with legacy systems and reduce the need for frequent hardware replacement or upgrades.",
    "category": "perception",
    "cost": 10500,
    "perceptionImpact": 17,
    "educationalMessage":
      "Software sustainability is not only about using less energy today, it also means avoiding unnecessary hardware turnover by keeping older devices and legacy systems usable for longer.",
    "realWorldEvidence":
      "Blue Angel style software sustainability criteria include efficient operation, reduced unnecessary background activity, and support for extending hardware lifespan.",
    "source": "Blue Angel for Software",
    "sourceUrl":
      "https://www.blauer-engel.de/en/products/electric-devices/software-products",
  },
];
